import { Helmet } from 'react-helmet-async';
import React, { useEffect, useState } from 'react';
import useSnackbar from 'src/hooks/useCustomSnackbar';
// @mui
import { Box, Container, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSettingsContext } from 'src/components/settings/context';
import { useLocation } from 'react-router';
import { updateSelectedPracticeForMenu } from 'src/redux/slices/mergeTags';
// components
import SegmentedControl from 'src/components/settings/SegmentedControl';
import MobileSettingsNav from 'src/components/settings/MobileSettingsNav';
import { useAuth } from '../../../hooks';
// sections
import { Practice, PracticeSocialLinks, PracticeBranding } from '../../../sections/@dashboard/practiceUser';
import OpenHourTabs from '../../../sections/@dashboard/practiceUser/OpenHourTabs';
// redux
import { useSelector, dispatch } from '../../../redux/store';
import { clearMessage } from '../../../redux/slices/practiceUser';
import { getPracticeItem } from '../../../redux/slices/singlePractice';

// ----------------------------------------------------------------------

const TABS = [
    { value: 'details', label: 'Practice Details', icon: 'ic:round-account-box' },
    { value: 'hours', label: 'Opening Hours', icon: 'solar:clock-circle-bold' },
    { value: 'social', label: 'Social Links', icon: 'eva:share-outline' },
    { value: 'branding', label: 'Branding', icon: 'tabler:brand-ubuntu' },
];

// ----------------------------------------------------------------------

export default function PracticeDetails() {

    const settings = useSettingsContext();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();

    const [currentTab, setCurrentTab] = useState('details');

    const practiceData = useSelector((state) => state.singlePractice);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { message, error } = useSelector((state) => state.practiceUser);
    const practiceUserResults = useSelector((state) => state.practiceUser?.practiceUser?.results);

    // Compute canEdit: ADMIN or superuser can edit; USER/TCO get read-only
    const currentPracticeUser = practiceUserResults?.find((u) => u.user?.id === user?.pk);
    const currentRole = currentPracticeUser?.role || '';
    const isSuperuser = Boolean(user?.is_superuser || currentPracticeUser?.user?.is_superuser);
    const canEdit = isSuperuser || currentRole === 'ADMIN';

    useEffect(() => {
        if (practiceId) {
            dispatch(getPracticeItem(practiceId));
        }
    }, [practiceId]);

    useEffect(() => {
        if (message) {
            enqueueSnackbar(message, { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            setTimeout(() => {
                dispatch(clearMessage());
            }, 3000);
        }
        if (error) {
            enqueueSnackbar(error, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            setTimeout(() => {
                dispatch(clearMessage());
            }, 3000);
        }
    }, [message, error, enqueueSnackbar]);

    const location = useLocation();

    useEffect(() => {
        dispatch(updateSelectedPracticeForMenu('practice_details'));
    }, [location.pathname]);

    const isLoaded = practiceData.practice && Object.keys(practiceData.practice).length > 0;

    return (
        <>
            <Helmet>
                <title> Practice Details | Dental SEM CRM </title>
            </Helmet>
            <Container maxWidth={settings.themeStretch ? false : 'xl'}>
                {/* Mobile settings nav */}
                <MobileSettingsNav />

                {/* Header */}
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                        Practice Details
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 2 }}>
                        Manage your practice information, opening hours, social links, and branding.
                    </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {/* Segmented Control Tabs */}
                {isLoaded && (
                    <Box sx={{ mb: 3 }}>
                        <SegmentedControl
                            tabs={TABS}
                            value={currentTab}
                            onChange={setCurrentTab}
                        />
                    </Box>
                )}

                {/* Tab Content */}
                {isLoaded && (
                    <Box>
                        {currentTab === 'details' && (
                            <Practice
                                practiceDetails={practiceData.practice}
                                practiceId={practiceId}
                                canEdit={canEdit}
                            />
                        )}
                        {currentTab === 'hours' && (
                            <OpenHourTabs
                                hoursList={practiceData.practice.opening_hours}
                                practiceId={practiceId}
                                canEdit={canEdit}
                            />
                        )}
                        {currentTab === 'social' && (
                            <PracticeSocialLinks
                                socialLinks={practiceData.practice.social_medias}
                                practiceId={practiceId}
                                canEdit={canEdit}
                            />
                        )}
                        {currentTab === 'branding' && (
                            <PracticeBranding
                                branding={practiceData.practice.branding}
                                practiceId={practiceId}
                                canEdit={canEdit}
                                practiceData={practiceData.practice}
                            />
                        )}
                    </Box>
                )}
            </Container>
        </>
    );
}
